import { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@apollo/client/react'
import { styled } from '@mui/material/styles'
import * as R from 'ramda'
import { v4 as uuid } from 'uuid'
import {
  type BasePuiDialogProps,
  ButtonWithLoader,
  type Nil,
  PuiDialog,
} from '@pbt/pbt-ui-components'

import { DisableMembershipRenewalDocument } from '~/api/graphql/generated/types'
import DialogNames from '~/constants/DialogNames'
import { WpCancelPaymentType } from '~/constants/paymentTypes'
import {
  cancelWellnessPlan,
  clearCancelWellnessPlanError,
  clearCancelWellnessPlanSuccess,
} from '~/store/actions/wellnessPlans'
import { addError } from '~/store/duck/errors'
import { patientMembershipsSelectors } from '~/store/duck/patientMemberships'
import { getPatientName } from '~/store/reducers/patients'
import {
  getCancelWellnessPlanError,
  getCancelWellnessPlanSuccess,
  getWellnessPlansIsCancelling,
} from '~/store/reducers/wellnessPlans'
import type { DataHandle, Membership } from '~/types'
import { getApolloErrorMessage } from '~/utils/errors'
import useCloseAfterCreation from '~/utils/useCloseAfterCreation'
import useDialog from '~/utils/useDialog'

import CancelMembershipDetails from './CancelMembershipDetails'
import CancelMembershipFailureAlert from './CancelMembershipFailureAlert'
import type { CancelData } from './cancelMembershipUtils'
import { CancelTiming } from './cancelMembershipUtils'

const StyledButton = styled(ButtonWithLoader)`
  min-width: 170px;
`

const StyledPuiDialog = styled(PuiDialog)`
  & .MuiDialog-paper {
    width: 650px;
    max-width: 650px;
  }

  & .MuiDialogActions-root {
    padding: ${({ theme }) => theme.spacing(1, 2)};
  }
`

interface CancelMembershipDialogV2Props extends BasePuiDialogProps {
  onCancelMembership?: () => void
  patientId: string | Nil
}

const CancelMembershipDialogV2 = ({
  patientId,
  open,
  onClose,
  onCancelMembership,
}: CancelMembershipDialogV2Props) => {
  const dispatch = useDispatch()
  const { t } = useTranslation(['Common', 'Memberships'])
  const navigate = useNavigate()

  const membership: Membership | Nil = useSelector(
    patientMembershipsSelectors.getItem(patientId),
  )
  const clientId = membership?.clientId
  const patientName = useSelector(getPatientName(patientId))
  const isCancelling = useSelector(getWellnessPlansIsCancelling)
  const cancelWellnessPlanSuccess = useSelector(getCancelWellnessPlanSuccess)
  const cancelWellnessPlanError = useSelector(getCancelWellnessPlanError)

  const [openPatientMembershipDialog] = useDialog(
    DialogNames.PATIENT_MEMBERSHIP,
  )

  const isRefund = membership?.cancellationType === WpCancelPaymentType.REFUND
  const hasCancelError = !R.isNil(cancelWellnessPlanError)

  const [
    disableMembershipRenewal,
    {
      data: disableRenewalData,
      loading: isDisableRenewalLoading,
      error: disableRenewalError,
    },
  ] = useMutation(DisableMembershipRenewalDocument)

  const cancellationDetailsRef = useRef<DataHandle<CancelData>>()

  useEffect(() => {
    if (disableRenewalError) {
      const errorInstance = new Error(
        getApolloErrorMessage(disableRenewalError),
      )
      const errorId = uuid()
      const url = window.location.href
      const status = 500

      dispatch(addError(errorInstance, status, errorId, url))
    }
  }, [disableRenewalError, dispatch])

  const closeDialog = useCallback(() => {
    dispatch(clearCancelWellnessPlanSuccess())
    dispatch(clearCancelWellnessPlanError())
    onClose?.()
  }, [dispatch, onClose])

  const handleClose = useCallback(() => {
    closeDialog()
    onCancelMembership?.()
  }, [closeDialog, onCancelMembership])

  const setCloseOnCancelledOn = useCloseAfterCreation(
    handleClose,
    getWellnessPlansIsCancelling,
  )

  useEffect(() => {
    if (cancelWellnessPlanSuccess) {
      setCloseOnCancelledOn()
    }
  }, [cancelWellnessPlanSuccess, setCloseOnCancelledOn])

  useEffect(() => {
    if (disableRenewalData && !isDisableRenewalLoading) {
      handleClose()
    }
  }, [disableRenewalData, isDisableRenewalLoading, handleClose])

  const cancelImmediately = (cancelData: CancelData) => {
    if (!clientId || !patientId) {
      return
    }

    const paymentAmount = isRefund
      ? cancelData.refundClient
      : cancelData.chargeClient

    dispatch(
      cancelWellnessPlan(
        clientId,
        patientId,
        {
          cancellationAmount: paymentAmount ?? 0,
          cancellationType: membership?.cancellationType,
          creditOrWriteOffAmount: cancelData.writeOff ?? 0,
          reason: {
            reason: cancelData.reason,
            notes: cancelData.notes,
          },
        },
        membership?.confirmToken,
      ),
    )
  }

  const cancelAtEndOfTerm = (cancelData: CancelData) => {
    const membershipId = membership?.plans?.[0]?.planLogId

    if (!membershipId) {
      return
    }

    disableMembershipRenewal({
      variables: {
        input: {
          membershipId,
          cancelReason: {
            reason: cancelData.reason,
            notes: cancelData.notes,
          },
        },
      },
    })
  }

  const handleViewTotalPaid = () => {
    if (!clientId) return
    closeDialog()
    navigate(`/membership/${clientId}/payments`)
  }

  const handleViewTotalUsed = () => {
    if (!clientId || !patientId) return
    closeDialog()
    openPatientMembershipDialog({
      clientId,
      patientId,
    })
  }

  const handleSubmit = () => {
    if (!cancellationDetailsRef.current?.validate()) {
      return
    }

    const cancelData = cancellationDetailsRef.current?.get()

    if (!cancelData) {
      return
    }

    if (cancelData.cancelTiming === CancelTiming.IMMEDIATELY) {
      cancelImmediately(cancelData)
    } else {
      cancelAtEndOfTerm(cancelData)
    }
  }

  const planName = membership?.planName || ''
  const membershipInfo = planName ? `${patientName} | ${planName}` : patientName
  const dialogTitle = `${t('Common:CANCEL_ACTION')}: ${membershipInfo}`

  return (
    <StyledPuiDialog
      actions={
        <StyledButton
          disabled={isCancelling || isDisableRenewalLoading}
          loading={isCancelling || isDisableRenewalLoading}
          onClick={handleSubmit}
        >
          {t('Common:SUBMIT_ACTION')}
        </StyledButton>
      }
      aria-labelledby="cancel-membership-dialog-v2"
      open={open}
      scroll="paper"
      title={dialogTitle}
      onClose={handleClose}
    >
      <CancelMembershipDetails
        cancelFailureAlert={
          hasCancelError ? (
            <CancelMembershipFailureAlert
              clientId={clientId}
              isRefund={isRefund}
              patientId={patientId}
              setCloseOnCancelledOn={setCloseOnCancelledOn}
            />
          ) : null
        }
        membership={membership}
        ref={cancellationDetailsRef}
        onViewTotalPaid={handleViewTotalPaid}
        onViewTotalUsed={handleViewTotalUsed}
      />
    </StyledPuiDialog>
  )
}

export default CancelMembershipDialogV2
